import { useState, useMemo, useCallback } from 'react';
import type { JobThread } from '../types/foreman';
import type { SessionInfo } from '../types/gateway';

export interface UseThreadsReturn {
  threads: JobThread[];
  filteredThreads: JobThread[];
  activeThreadId: string | null;
  setActiveThreadId: (id: string | null) => void;
}

/**
 * Derives thread list from real OpenClaw sessions.
 * Each session becomes a thread in the sidebar.
 */
export function useThreads(sessions: SessionInfo[], activeSessionId: string | null): UseThreadsReturn {
  const [activeThreadId, setActiveThreadIdState] = useState<string | null>(activeSessionId);

  // Keep in sync with session changes
  const effectiveActiveId = activeSessionId ?? activeThreadId;

  // Convert sessions to JobThread format
  const threads: JobThread[] = useMemo(
    () =>
      sessions.map((s) => ({
        id: s.id,
        name: s.name || s.id,
        code: s.id === 'main' ? 'MAIN' : s.id.toUpperCase().slice(0, 12),
        status: 'active' as const,
        unread: 0,
        lastMessage: s.metadata?.lastMessage as string || '',
        lastMessageAt: s.lastMessageAt || s.createdAt,
        role: 'designer' as const, // All threads visible to all roles for now
      })),
    [sessions]
  );

  // No role filtering — show all threads
  const filteredThreads = threads;

  const handleSetActiveThread = useCallback((id: string | null) => {
    setActiveThreadIdState(id);
  }, []);

  return {
    threads,
    filteredThreads,
    activeThreadId: effectiveActiveId,
    setActiveThreadId: handleSetActiveThread,
  };
}
